import type {
  CacheStats,
  DistrictStat,
  PerformanceWindow,
  RouteStats,
} from "./analyticsApi";
import { ANALYTICS_WINDOWS } from "./analyticsTable";

export type Population = "users" | "probe";

const ROUTE_LABELS: readonly [string, string][] = [
  ["page:dashboard", "Dashboard · browser"],
  ["page:progress-reports", "Progress Reports page · browser"],
  ["progress-report-list", "Progress Reports list · server"],
  ["page:roster", "Roster page · browser"],
  ["roster-classification", "Roster classification · server"],
  ["student-progress-report", "Student progress report · server"],
  ["roster-snapshot", "Roster snapshot refresh · server"],
  ["monitoring-refresh", "Monitoring refresh · server"],
  ["growth-tile", "Growth tile · server"],
  ["growth-tile-refresh", "Growth tile refresh · server"],
  ["growth-district", "Growth district page · server"],
  ["growth-grade", "Growth grade drill-down · server"],
  ["growth-school", "Growth school drill-down · server"],
  ["growth-teacher", "Growth teacher drill-down · server"],
  ["growth-student", "Growth student drill-down · server"],
  ["district-stats", "Dashboard stats · server"],
  ["licenses", "Licenses · server"],
  ["benchmark-monitoring", "Benchmark monitoring tile · server"],
  ["assignment-monitoring", "Assignment monitoring · server"],
  ["implementation-report", "Implementation report · server"],
  ["student-benchmarks", "Student benchmarks panel · server"],
  ["student-assignments", "Student assignments panel · server"],
  ["impersonation-users", "Impersonate user list · server"],
];

export function routeLabel(route: string): string {
  return ROUTE_LABELS.find(([key]) => key === route)?.[1] ?? route;
}

function routeOrder(route: string): number {
  const index = ROUTE_LABELS.findIndex(([key]) => key === route);
  return index === -1 ? ROUTE_LABELS.length : index;
}

export type PerformanceCell = {
  window: string;
  count: number;
  failures: number;
  unconfigured: number;
  p50Ms: number;
  p90Ms: number;
  maxMs: number;
  live: number;
  liveLoads: number;
};

export type PerformanceRow = {
  route: string;
  label: string;
  cells: (PerformanceCell | null)[];
};

function routesOf(
  window: PerformanceWindow,
  population: Population,
): RouteStats[] {
  return population === "users" ? window.users : window.probe.routes;
}

function windowNamed(
  windows: PerformanceWindow[],
  name: string,
): PerformanceWindow | undefined {
  return windows.find((w) => w.window === name);
}

export function performanceRows(
  windows: PerformanceWindow[],
  population: Population,
): PerformanceRow[] {
  const routes = [
    ...new Set(
      windows.flatMap((w) => routesOf(w, population).map((r) => r.route)),
    ),
  ];
  routes.sort((a, b) => routeOrder(a) - routeOrder(b) || a.localeCompare(b));
  return routes.map((route) => ({
    route,
    label: routeLabel(route),
    cells: ANALYTICS_WINDOWS.map(({ key }) => {
      const stats =
        windowNamed(windows, key) &&
        routesOf(windowNamed(windows, key)!, population).find(
          (r) => r.route === route,
        );
      return stats
        ? {
            window: key,
            count: stats.count,
            failures: stats.failures,
            unconfigured: stats.unconfigured,
            p50Ms: stats.p50Ms,
            p90Ms: stats.p90Ms,
            maxMs: stats.maxMs,
            live: stats.live,
            liveLoads: stats.liveLoads,
          }
        : null;
    }),
  }));
}

export type Pace = "green" | "yellow" | "red";

const PACE_YELLOW_FROM_TENTHS = 4;
const PACE_RED_OVER_TENTHS = 15;

function tenthsOf(milliseconds: number): number {
  return Math.round(Math.max(0, milliseconds) / 100);
}

export function paceOf(milliseconds: number): Pace {
  const tenths = tenthsOf(milliseconds);
  if (tenths > PACE_RED_OVER_TENTHS) return "red";
  if (tenths >= PACE_YELLOW_FROM_TENTHS) return "yellow";
  return "green";
}

export function formatSeconds(milliseconds: number): string {
  return `${(tenthsOf(milliseconds) / 10).toFixed(1)} s`;
}

export function loadsCaption({
  count,
  failures,
  unconfigured = 0,
  live = 0,
  liveLoads = 0,
}: {
  count: number;
  failures: number;
  unconfigured?: number;
  live?: number;
  liveLoads?: number;
}): string {
  const parts = [`${count} ${count === 1 ? "load" : "loads"}`];
  if (failures > 0) parts.push(`${failures} failed`);
  if (unconfigured > 0) parts.push(`${unconfigured} not configured`);
  if (liveLoads > 0) {
    const calls = `${live} live ${live === 1 ? "call" : "calls"}`;
    parts.push(
      liveLoads < count
        ? `${calls} over ${liveLoads} ${liveLoads === 1 ? "load" : "loads"}`
        : calls,
    );
  }
  return parts.join(" · ");
}

export function slowestFor(
  windows: PerformanceWindow[],
  population: Population,
  window: string,
  route: string,
): DistrictStat[] {
  const named = windowNamed(windows, window);
  if (!named) return [];
  return (
    routesOf(named, population).find((r) => r.route === route)?.slowest ?? []
  );
}

export function probeSummary(
  windows: PerformanceWindow[],
  window: string,
): { lastRunAt: string | undefined; districts: number } {
  const named = windowNamed(windows, window);
  return {
    lastRunAt: named?.probe.lastRunAt,
    districts: named?.probe.districts ?? 0,
  };
}

export function hasAnyLoads(
  windows: PerformanceWindow[],
  population: Population,
): boolean {
  return windows.some((w) => routesOf(w, population).some((r) => r.count > 0));
}

const CACHE_LABELS: readonly [string, string][] = [
  ["roster-snapshot", "Flashlight360 roster snapshot"],
  ["benchmark-cache", "Benchmark cache"],
];

export function cacheLabel(cache: string): string {
  return CACHE_LABELS.find(([key]) => key === cache)?.[1] ?? cache;
}

function cacheOrder(cache: string): number {
  const index = CACHE_LABELS.findIndex(([key]) => key === cache);
  return index === -1 ? CACHE_LABELS.length : index;
}

export type CacheCell = CacheStats & { window: string };

export type CacheRow = {
  cache: string;
  label: string;
  cells: (CacheCell | null)[];
};

export function cacheRows(windows: PerformanceWindow[]): CacheRow[] {
  const caches = [
    ...new Set(windows.flatMap((w) => (w.caches ?? []).map((c) => c.cache))),
  ];
  caches.sort((a, b) => cacheOrder(a) - cacheOrder(b) || a.localeCompare(b));
  return caches.map((cache) => ({
    cache,
    label: cacheLabel(cache),
    cells: ANALYTICS_WINDOWS.map(({ key }) => {
      const stats = windowNamed(windows, key)?.caches?.find(
        (c) => c.cache === cache,
      );
      return stats ? { ...stats, window: key } : null;
    }),
  }));
}

export function hasAnyCacheReads(windows: PerformanceWindow[]): boolean {
  return windows.some((w) => (w.caches ?? []).some((c) => c.reads > 0));
}

const AGE_MINUTE = 60_000;
const AGE_HOUR = 60 * AGE_MINUTE;
const AGE_DAY = 24 * AGE_HOUR;

export function formatAge(milliseconds: number): string {
  const elapsed = Math.max(0, milliseconds);
  if (elapsed < AGE_MINUTE) return "under a minute old";
  if (elapsed < AGE_HOUR) return `${Math.floor(elapsed / AGE_MINUTE)} min old`;
  if (elapsed < AGE_DAY) {
    const hours = Math.floor(elapsed / AGE_HOUR);
    const minutes = Math.floor((elapsed % AGE_HOUR) / AGE_MINUTE);
    return minutes > 0 ? `${hours} h ${minutes} min old` : `${hours} h old`;
  }
  const days = Math.floor(elapsed / AGE_DAY);
  const hours = Math.floor((elapsed % AGE_DAY) / AGE_HOUR);
  return hours > 0 ? `${days} d ${hours} h old` : `${days} d old`;
}

export function readsCaption(reads: number): string {
  if (reads === 0) return "No reads";
  return `${reads} ${reads === 1 ? "read" : "reads"}`;
}
